import { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { Job, OutletContext, PlatformApp, PlatformAppInstance } from '@typings';
import { getPlatformAppInstanceEvents } from '@services';
import { contextNamesSelector } from '@selectors';
import { useHelmetTitle, useSelector } from '@hooks';
import { capitalize, invariant, toastifyResponseError } from '@utils';

import { ChaseSpinner, Helmet, Theme } from '@components';
import { Layout } from '@components/Layouts';
import { PlatformAppsInstanceTabs } from '@components/Ui';
import { PlatformAppManagementHandleCenter } from '@components/Ui/PlatformApps';

type Props = {
  instance: PlatformApp.Instance;
  input: OutletContext.PlatformAppsInstance['input'];
  output: OutletContext.PlatformAppsInstance['output'];
  getInstance: () => Promise<PlatformApp.Instance>;
  getInstanceParams: () => void;
};

export const PlatformAppManagementLayout: React.FC<Props> = ({
  instance,
  input,
  output,
  instance: { displayName, name, id: instanceId },
  getInstance,
  getInstanceParams,
}) => {
  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);

  const { makeTitle } = useHelmetTitle();

  const [tabTitle, setTabTitle] = useState<Job.Tab>();
  const [events, setEvents] = useState<PlatformAppInstance.Event[] | null>(
    null,
  );

  const title = makeTitle(displayName ?? name, tabTitle);

  const handleTabTitleChange = useCallback((tab: Job.Tab) => {
    setTabTitle(capitalize(tab) as Job.Tab);
  }, []);

  const getEvents = useCallback(async () => {
    try {
      invariant(clusterName);
      invariant(organizationName);
      invariant(projectName);
      invariant(instanceId);

      const { items: events } = await getPlatformAppInstanceEvents({
        organizationName,
        clusterName,
        projectName,
        instanceId,
      });

      setEvents(events);
    } catch (error) {
      toastifyResponseError(error);
    }
  }, [organizationName, clusterName, projectName, instanceId]);

  useEffect(() => {
    const run = async () => {
      try {
        await getEvents();
      } catch (error) {
        /**
         * Continue regardless error
         */
      }
    };

    run();

    const intervalId = setInterval(() => {
      getEvents();
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [getEvents]);

  const refreshInstance = async () => {
    try {
      await getInstance();
      getInstanceParams();
      getEvents();
    } catch (error) {
      /**
       * Continue regardless error
       */
    }
  };

  const renderContent = () => {
    if (!instance) {
      return (
        <Layout.Content className="relative flex justify-center">
          <ChaseSpinner color="black" className="absolute mt-40 h-14 w-14" />
        </Layout.Content>
      );
    }

    return (
      <Layout.Content>
        <PlatformAppManagementHandleCenter
          state={instance.state}
          instanceId={instanceId}
          endpoints={instance.endpoints}
          createdAt={instance.createdAt}
          getInstance={refreshInstance}
        />
        <Theme.Container className="relative p-0">
          <PlatformAppsInstanceTabs instanceId={instanceId} />
          <Outlet
            context={{
              instance,
              input,
              output,
              events,
              getInstance,
              setTabTitle: handleTabTitleChange,
            }}
          />
        </Theme.Container>
      </Layout.Content>
    );
  };

  return (
    <Layout backNavigation title={title}>
      <Helmet title={title} />
      {renderContent()}
    </Layout>
  );
};
